<script lang="ts" setup>
  import {
    DROPDOWN_CONTEXT_KEY,
    DropdownApplyFooter,
    DropdownPanelScroll,
    Radio,
    RadioGroup,
    RadioGroupOrientation,
  } from '@tundr/ds-components';
  import { inject, ref, watch } from 'vue';
  import { useFilters } from '../../../../composables';
  import type {
    SelectFilterOptions,
    SingleSelectModelValue,
  } from '../../../../store';
  import { FilterTypes, getSelectOptionKey } from '../../../../utils';
  import { optionRowClass } from './SelectOneFilter.css';

  const props = defineProps<{
    filterNamespace: string;
    filterLabel: string;
    filterFieldName: string;
    options: SelectFilterOptions;
    modelValue?: SingleSelectModelValue;
  }>();

  defineOptions({
    inheritAttrs: false,
  });

  const dropdown = inject(DROPDOWN_CONTEXT_KEY, null);

  const currentFilterStore = useFilters(props.filterNamespace);

  const selectedItem = ref<SingleSelectModelValue | undefined>(
    props.modelValue,
  );

  const selectedOption = ref<string | undefined>(
    getSelectOptionKey(props.modelValue?.value),
  );

  watch(
    () => props.modelValue,
    (value) => {
      selectedItem.value = value;
      selectedOption.value = getSelectOptionKey(value?.value);
    },
  );

  const emit = defineEmits<{
    'update:modelValue': [SingleSelectModelValue | undefined];
  }>();

  const onApplyFilter = () => {
    emit('update:modelValue', selectedItem.value);
    currentFilterStore.applyFilterValue({
      filterFieldName: props.filterFieldName,
      value: selectedItem.value,
      filterType: FilterTypes.SELECT,
    });
    dropdown?.close();
  };

  const onSelectItem = (item: SingleSelectModelValue) => {
    selectedItem.value = item;
    selectedOption.value = getSelectOptionKey(item.value);
  };

  const onSelectByValue = (value: string) => {
    const option = props.options.find(
      (o) => getSelectOptionKey(o.value) === value,
    );
    if (option) onSelectItem(option);
  };
</script>

<template>
  <DropdownPanelScroll role="group" :aria-label="filterLabel">
    <RadioGroup
      :model-value="selectedOption ?? ''"
      :name="filterFieldName"
      :orientation="RadioGroupOrientation.vertical"
      :with-gap="false"
      @update:model-value="onSelectByValue"
    >
      <Radio
        v-for="option in options"
        :key="getSelectOptionKey(option.value)"
        :class="optionRowClass"
        :value="getSelectOptionKey(option.value)"
        :label="option.label ?? getSelectOptionKey(option.value)"
      />
    </RadioGroup>
  </DropdownPanelScroll>
  <DropdownApplyFooter :disabled="!selectedItem" @apply="onApplyFilter" />
</template>
